import React from "react";

import { usePsychologyMap, ENTRY_TYPES } from "../../../../services/usePsychologyMap";

/**
 * Workstation · Ads & Creative — **step 1, Psychology Map, in v2.**
 *
 * WAVE 3, SCREEN 1. The look is v2's; the behaviour is `usePsychologyMap`,
 * which v1 also calls. One implementation, two presentations — the same
 * discipline as the resume rule in wave 2b, and for the same reason: two copies
 * of one rule against one database eventually disagree, and the operator gets
 * whichever screen they happened to open.
 *
 * WHAT THE SWEEP FOUND AND FIXED BEFORE THIS PORT: Regenerate had never
 * regenerated. It hit an endpoint that short-circuits on an existing map,
 * returned the cached one, and the screen appended it — duplicating the whole
 * map on every click. Live: GRANT001 held 60 entries with 15 unique. Fixed in
 * the hook, so v1 got the fix too.
 *
 * Errors go through `errorText()` (inside the hook) — landed in wave 2b before
 * any step used it, because the wave-1 crash came from rewriting an error path.
 */

const AWARENESS_STAGES = [
  "UNAWARE", "PROBLEM_AWARE", "SOLUTION_AWARE", "PRODUCT_AWARE", "MOST_AWARE",
];

export default function PsychologyMapStep({ offer, onComplete, isNewSession }) {
  const {
    entries, loading, generating, feedback, setFeedback,
    awarenessStage, setAwarenessStage, mapNote, mapError,
    generateMap, confirmAndProceed, togglePriority, removeEntry,
    updateEntryEmotion,
  } = usePsychologyMap({ offer, isNewSession, onComplete });

  const byType = ENTRY_TYPES.map((type) => ({
    type,
    rows: entries
      .map((e, i) => ({ e, i }))
      .filter(({ e }) => (e.entry_point_type || "").toUpperCase() === type),
  })).filter((g) => g.rows.length > 0);

  const untyped = entries
    .map((e, i) => ({ e, i }))
    .filter(({ e }) => !ENTRY_TYPES.includes((e.entry_point_type || "").toUpperCase()));

  return (
    <div className="ws-step" data-testid="ws-psychology-step">
      <div className="ws-step-head">
        <div>
          <h3 className="ws-step-title">Audience Psychology Map</h3>
          <p className="dim">
            The psychological entry points this offer's creative will work from.
          </p>
        </div>
        <button
          type="button"
          className={entries.length === 0 ? "btn-primary btn-sm" : "btn-ghost btn-sm"}
          onClick={() => generateMap(entries.length > 0)}
          disabled={generating}
          data-testid={entries.length === 0 ? "ws-psychology-generate" : "ws-psychology-regenerate"}
        >
          {generating
            ? (entries.length === 0 ? "Generating…" : "Regenerating…")
            : (entries.length === 0 ? "Generate Psychology Map" : "Regenerate")}
        </button>
      </div>

      {/* What the last run actually did. Silent when it did the obvious thing. */}
      {mapNote ? (
        <p className="ws-step-note" role="status" data-testid="ws-psychology-note">
          {mapNote.text}
          {/* A repeat click that produces the identical sentence must still be
              visibly a repeat, or it reads as a dead button. */}
          {mapNote.count > 1 ? (
            <span className="dim"> (asked {mapNote.count} times)</span>
          ) : null}
        </p>
      ) : null}

      {/* A FAILED SAVE IS NAMED HERE, not in a browser dialog. Founder,
          2026-09-09. It sits apart from the note above because a result and a
          failure must not share a slot or a style. */}
      {mapError ? (
        <p className="ws-step-note state-error" role="alert"
           data-testid="ws-psychology-error">{mapError}</p>
      ) : null}

      {entries.length === 0 ? (
        <div className="state ws-state" data-testid="ws-psychology-empty">
          No psychology map yet. Generate one to begin — everything downstream
          reads from it.
        </div>
      ) : (
        <>
          <div className="ws-psych-groups">
            {byType.map((group) => (
              <section key={group.type} className="ws-psych-group">
                <h4 className="ws-psych-group-title">
                  {group.type.replace(/_/g, " ")}
                  <span className="dim"> · {group.rows.length}</span>
                </h4>
                <ul className="ws-psych-list">
                  {group.rows.map(({ e, i }) => (
                    <li
                      key={`${e.statement}-${i}`}
                      className={`ws-psych-entry ${e.priority_flagged ? "is-priority" : ""}`}
                      data-testid={`ws-psychology-entry-${i}`}
                    >
                      <p className="ws-psych-statement">{e.statement}</p>
                      <div className="ws-psych-controls">
                        <label className="ws-psych-emotion">
                          <span className="dim">Emotion</span>
                          <input
                            type="text"
                            value={e.primary_emotion || ""}
                            onChange={(ev) => updateEntryEmotion(i, ev.target.value)}
                            data-testid={`ws-psychology-emotion-${i}`}
                          />
                        </label>
                        <button
                          type="button"
                          className="btn-ghost btn-sm"
                          onClick={() => togglePriority(i)}
                          aria-pressed={!!e.priority_flagged}
                          data-testid={`ws-psychology-priority-${i}`}
                        >
                          {e.priority_flagged ? "★ Priority" : "☆ Priority"}
                        </button>
                        <button
                          type="button"
                          className="btn-ghost btn-sm"
                          onClick={() => removeEntry(i)}
                          data-testid={`ws-psychology-remove-${i}`}
                        >
                          Remove
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            ))}

            {/* An entry whose type is not one of the five is shown, not hidden.
                A screen that silently drops rows it does not recognise is how
                you lose work and never find out. */}
            {untyped.length > 0 ? (
              <section className="ws-psych-group" data-testid="ws-psychology-untyped">
                <h4 className="ws-psych-group-title">
                  Unrecognised type<span className="dim"> · {untyped.length}</span>
                </h4>
                <ul className="ws-psych-list">
                  {untyped.map(({ e, i }) => (
                    <li key={`u-${i}`} className="ws-psych-entry">
                      <p className="ws-psych-statement">{e.statement}</p>
                      <p className="dim">
                        Type recorded as “{e.entry_point_type || "(none)"}”.
                      </p>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}
          </div>

          <div className="ws-step-foot">
            <label className="ws-psych-stage">
              <span className="dim">Awareness stage this offer targets</span>
              <select
                value={awarenessStage}
                onChange={(e) => setAwarenessStage(e.target.value)}
                data-testid="ws-psychology-stage"
              >
                {AWARENESS_STAGES.map((a) => (
                  <option key={a} value={a}>{a.replace(/_/g, " ")}</option>
                ))}
              </select>
            </label>

            <div className="ws-psych-regen">
              <textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                rows={2}
                placeholder="What is wrong with these? The next generation will be told."
                data-testid="ws-psychology-feedback"
              />
              <button
                type="button"
                className="btn-primary btn-sm"
                onClick={confirmAndProceed}
                disabled={loading || entries.length === 0}
                data-testid="ws-psychology-confirm"
              >
                {loading ? "Saving…" : "Confirm & continue"}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
